import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1512403754473-27835f7b9984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBmaW5hbmNpYWwlMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4NDE5NDIwN3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome, ${res.user.name}`);
    nav("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left visual */}
      <div className="hidden lg:block relative">
        <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/60 to-blue-950/70" />
        <div className="relative h-full flex flex-col justify-between p-10 text-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl">Complio</div>
              <div className="text-xs uppercase tracking-widest text-slate-300">Compliance Tracker · AED · Dubai</div>
            </div>
          </div>
          <div className="max-w-md">
            <h2 className="font-display font-bold text-4xl leading-tight">
              Never miss a VAT, Audit, Corporate Tax or Excise deadline again.
            </h2>
            <p className="mt-4 text-slate-300 text-base leading-relaxed">
              Purpose-built for accounting and tax firms in the UAE. Assign work,
              track deadlines with clear colour cues, and get automatic in-app
              reminders for every filing period.
            </p>
          </div>
          <div className="text-xs text-slate-400 tracking-wide">© {new Date().getFullYear()} Complio</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="font-display font-extrabold text-xl text-slate-900">Complio</div>
          </div>
          <div className="text-[11px] tracking-[0.15em] uppercase font-semibold text-slate-500">
            Sign in
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Welcome back
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            Enter your work credentials to access the compliance workspace.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11"
                placeholder="you@firm.ae"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={submitting} className="w-full h-11 text-base" data-testid="login-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
